import { API_ENDPOINTS, getToken, setToken, removeToken } from '../config/api';

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  page: number;
  page_size: number;
  total_pages: number;
  results: T[];
}

export interface MaterialListQuery {
  page?: number;
  page_size?: number;
  q?: string;
  search?: string;
  flag?: string;
}

function buildListQuery(params?: MaterialListQuery): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.page_size) qs.set('page_size', String(params.page_size));
  const q = params.q ?? params.search;
  if (q?.trim()) qs.set('q', q.trim());
  if (params.flag?.trim()) qs.set('flag', params.flag.trim());
  const s = qs.toString();
  return s ? `?${s}` : '';
}

function normalizePaginated<T>(data: PaginatedResponse<T> | T[]): PaginatedResponse<T> {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      page: 1,
      page_size: data.length,
      total_pages: 1,
      results: data,
    };
  }
  return data;
}

export interface RegisterData {
  email: string;
  password: string;
  password_confirm: string;
  organization?: string;
  first_name?: string;
  last_name?: string;
}

export interface UsernameRegisterData {
  username: string;
  password: string;
  password_confirm: string;
  organization?: string;
  first_name?: string;
  last_name?: string;
}

export interface LoginData {
  password: string;
  email?: string;
  username?: string;
}

export interface PlatformSettingsPublic {
  allow_username_signup: boolean;
}

export interface PlatformSettingsAdmin extends PlatformSettingsPublic {
  updated_at: string;
}

export interface AuthResponse {
  user: {
    id: number;
    email: string | null;
    username?: string | null;
    organization?: string;
    first_name?: string;
    last_name?: string;
    is_active: boolean;
    is_staff?: boolean;
    is_superuser?: boolean;
    created_at: string;
  };
  token: string;
  message: string;
}

export interface UserProfile {
  id: number;
  email: string | null;
  username?: string | null;
  organization?: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  tutorial_completed?: boolean;
  created_at: string;
}

export interface ProfilePatchPayload {
  first_name?: string;
  last_name?: string;
  current_password?: string;
  new_password?: string;
  new_password_confirm?: string;
}

// Helper function to make API requests
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }

  const response = await fetch(endpoint, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    let msg =
      (typeof errorData.error === 'string' && errorData.error) ||
      (typeof errorData.message === 'string' && errorData.message) ||
      '';
    if (!msg && Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length) {
      msg = String(errorData.non_field_errors[0]);
    }
    if (!msg && typeof errorData.detail === 'string') {
      msg = errorData.detail;
    }
    if (!msg) {
      const key = Object.keys(errorData)[0];
      if (key) {
        const v = errorData[key];
        if (Array.isArray(v) && v.length) msg = String(v[0]);
        else if (typeof v === 'string') msg = v;
      }
    }
    throw new Error(msg || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Authentication API calls
export const authAPI = {
  /** Public (no auth header) — for sign-up / login UI. */
  getPublicPlatformSettings: async (): Promise<PlatformSettingsPublic> => {
    const res = await fetch(API_ENDPOINTS.PLATFORM_SETTINGS_PUBLIC);
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Register new user (email mode)
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await apiRequest<AuthResponse>(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (response.token) {
      setToken(response.token);
    }
    
    return response;
  },

  // Register with username (when server has allow_username_signup)
  registerWithUsername: async (data: UsernameRegisterData): Promise<AuthResponse> => {
    const response = await apiRequest<AuthResponse>(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.token) {
      setToken(response.token);
    }

    return response;
  },

  // Login user
  login: async (data: LoginData): Promise<AuthResponse> => {
    const body: Record<string, string> = { password: data.password };
    if (data.email !== undefined && data.email !== '') {
      body.email = data.email;
    }
    if (data.username !== undefined && data.username !== '') {
      body.username = data.username;
    }
    const response = await apiRequest<AuthResponse>(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    if (response.token) {
      setToken(response.token);
    }
    
    return response;
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      await apiRequest(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeToken();
    }
  },

  // Get user profile
  getProfile: async (): Promise<UserProfile> => {
    return apiRequest<UserProfile>(API_ENDPOINTS.PROFILE, {
      method: 'GET',
    });
  },

  patchProfile: async (data: ProfilePatchPayload): Promise<UserProfile> => {
    return apiRequest<UserProfile>(API_ENDPOINTS.PROFILE, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/** Staff: toggle username sign-up (same flag as Django admin Platform settings). */
export const adminPlatformSettingsAPI = {
  get: async (): Promise<PlatformSettingsAdmin> => {
    return apiRequest<PlatformSettingsAdmin>(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS, {
      method: 'GET',
    });
  },
  patch: async (data: Partial<PlatformSettingsPublic>): Promise<PlatformSettingsAdmin> => {
    return apiRequest<PlatformSettingsAdmin>(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

export interface HomePageServiceCard {
  title: string;
  items: string[];
}

export interface HomePageContentData {
  hero_title: string;
  hero_subtitle: string;
  cta_button_label: string;
  services_section_title: string;
  service_cards: HomePageServiceCard[];
  about_title: string;
  about_body: string;
  footer_tagline: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  instagram_url: string;
  telegram_url: string;
  footer_copyright: string;
}

export const homePageAPI = {
  getPublic: async (): Promise<HomePageContentData> => {
    const res = await fetch(API_ENDPOINTS.HOME_PAGE_CONTENT);
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }
    return res.json();
  },
  getAdmin: async (): Promise<HomePageContentData> => {
    return apiRequest<HomePageContentData>(API_ENDPOINTS.ADMIN_HOME_PAGE_CONTENT, {
      method: 'GET',
    });
  },
  putAdmin: async (data: HomePageContentData): Promise<HomePageContentData> => {
    return apiRequest<HomePageContentData>(API_ENDPOINTS.ADMIN_HOME_PAGE_CONTENT, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// Admin Stats API
export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalTests: number;
  completedTests: number;
  pendingTests: number;
  totalSessions: number;
}

export const adminStatsAPI = {
  getStats: async (): Promise<AdminStats> => {
    return apiRequest<AdminStats>(API_ENDPOINTS.ADMIN_STATS, {
      method: 'GET',
    });
  },
};

// Admin Mock Grading API
export interface MockSubmissionSummary {
  id: number;
  user_email: string;
  dialogue_name: string;
  dialogue_id: number;
  category_name: string;
  status: string;
  created_at: string;
  audios_count: number;
  overall_score?: number | null;
}

export interface MockSubmissionAudio {
  submission_audio_id: number;
  order: number;
  system_audio: AudioFile & { source_text?: string; translations?: string[] };
  user_recording: AudioFile & { transcript?: string };
  /** Set on graded submissions (admin read-only detail). */
  criterion_scores?: Record<string, number> | null;
}

export interface MockSubmissionDetail {
  id: number;
  user_email: string;
  dialogue_name: string;
  dialogue_id: number;
  status: string;
  created_at: string;
  audios: MockSubmissionAudio[];
  /** True when submission is already graded — no submit, scores read-only. */
  readonly?: boolean;
  admin_overall_feedback?: string;
  overall_score?: number | null;
}

function normalizeMockSubmissionList(raw: unknown): MockSubmissionSummary[] {
  if (Array.isArray(raw)) return raw as MockSubmissionSummary[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as { results?: unknown }).results)) {
    return (raw as { results: MockSubmissionSummary[] }).results;
  }
  return [];
}

export const mockGradingAPI = {
  listSubmissions: async (
    status: 'pending' | 'graded' | 'all' = 'pending'
  ): Promise<MockSubmissionSummary[]> => {
    const q = status === 'pending' ? '' : `?status=${encodeURIComponent(status)}`;
    const raw = await apiRequest<unknown>(`${API_ENDPOINTS.ADMIN_MOCK_SUBMISSIONS}${q}`, {
      method: 'GET',
    });
    return normalizeMockSubmissionList(raw);
  },
  listPending: async (): Promise<MockSubmissionSummary[]> => {
    return mockGradingAPI.listSubmissions('pending');
  },
  getDetail: async (submissionId: number): Promise<MockSubmissionDetail> => {
    return apiRequest<MockSubmissionDetail>(API_ENDPOINTS.ADMIN_MOCK_SUBMISSION_DETAIL(submissionId), { method: 'GET' });
  },
  submitResults: async (
    submissionId: number,
    grades: {
      submission_audio_id: number;
      distortion: number;
      unjustified_omission: number;
      unjustified_insertion: number;
      inappropriate_register: number;
      unidiomatic_expression: number;
      grammar_syntax_error: number;
      spelling_error: number;
      punctuation_error: number;
    }[],
    adminOverallFeedback?: string
  ): Promise<{ message: string }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_MOCK_SUBMISSION_SUBMIT_RESULTS(submissionId), {
      method: 'POST',
      body: JSON.stringify({
        grades,
        admin_overall_feedback: adminOverallFeedback ?? '',
      }),
    });
  },
};

// Tutorial API calls
export const tutorialAPI = {
  // Mark tutorial as completed
  completeTutorial: async (): Promise<{ message: string; tutorial_completed: boolean }> => {
    return apiRequest<{ message: string; tutorial_completed: boolean }>(API_ENDPOINTS.TUTORIAL_COMPLETE, {
      method: 'POST',
    });
  },
};

export interface UserStats {
  completed_segments: number;
  completed_podcasts: number;
  dialogues_practiced: number;
  mock_tests_submitted: number;
}

export interface RecentActivity {
  type: string;
  name: string;
  date: string;
  activity_type: 'test' | 'podcast' | 'material' | 'dialogue' | 'mock';
  practice_attempt_number?: number | null;
  detail?: string | null;
}

export const userStatsAPI = {
  getUserStats: async (): Promise<UserStats> => {
    return apiRequest<UserStats>(API_ENDPOINTS.USER_STATS, {
      method: 'GET',
    });
  },
  getRecentActivity: async (): Promise<RecentActivity[]> => {
    return apiRequest<RecentActivity[]>(API_ENDPOINTS.USER_RECENT_ACTIVITY, {
      method: 'GET',
    });
  },
};

export interface UserRecordingWithSegment {
  recording: AudioFile | null;
  attempts: number;
  system_audio: AudioFile;
  order: number | null;
  last_attempt_at: string | null;
}

export interface SegmentWithRecordings {
  segment: StructuralSegment;
  recordings: UserRecordingWithSegment[];
  total_attempts: number;
}

export interface AdminUserListItem extends UserProfile {
  last_login?: string | null;
  has_active_subscription?: boolean;
}

export interface AdminUsersStats {
  total: number;
  active: number;
  inactive: number;
  staff: number;
  subscribed: number;
}

export interface AdminUsersListQuery {
  page?: number;
  page_size?: number;
  q?: string;
  is_active?: boolean;
  is_staff?: boolean;
  has_subscription?: boolean;
  ordering?: string;
}

export interface UserDetails {
  user: UserProfile;
  all_recordings: AudioFile[];
  segments: SegmentWithRecordings[];
  total_recordings: number;
  total_segments: number;
}

export const adminUserManagementAPI = {
  listUsers: async (params?: AdminUsersListQuery): Promise<PaginatedResponse<AdminUserListItem>> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    if (params?.q?.trim()) qs.set('q', params.q.trim());
    if (typeof params?.is_active === 'boolean') qs.set('is_active', String(params.is_active));
    if (typeof params?.is_staff === 'boolean') qs.set('is_staff', String(params.is_staff));
    if (typeof params?.has_subscription === 'boolean') qs.set('has_subscription', String(params.has_subscription));
    if (params?.ordering) qs.set('ordering', params.ordering);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const data = await apiRequest<PaginatedResponse<AdminUserListItem> | AdminUserListItem[]>(
      `${API_ENDPOINTS.ADMIN_USERS_LIST}${suffix}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  getStats: async (): Promise<AdminUsersStats> => {
    return apiRequest<AdminUsersStats>(API_ENDPOINTS.ADMIN_USERS_STATS, { method: 'GET' });
  },

  updateUser: async (
    userId: number,
    data: { is_active?: boolean; is_staff?: boolean }
  ): Promise<AdminUserListItem> => {
    return apiRequest<AdminUserListItem>(API_ENDPOINTS.ADMIN_USER_UPDATE(userId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getUserDetails: async (userId: number): Promise<UserDetails> => {
    return apiRequest<UserDetails>(API_ENDPOINTS.ADMIN_USER_DETAILS(userId), {
      method: 'GET',
    });
  },
};

// Material API interfaces
export interface AudioFile {
  id: number;
  file_name: string;
  garage_key: string;
  file_size: number;
  file_size_mb: number;
  duration: number | null;
  audio_type: 'system' | 'user_recording';
  mime_type: string;
  segment_number: number | null;
  test_category: string | null;
  created_at: string;
  stream_url: string;
  is_active: boolean;
}

export interface UploadMaterialData {
  audio_file: File;
  segment_number?: number;
  test_category?: string;
}

export interface UpdateMaterialData {
  /** Omit to leave unchanged; send `null` to clear. */
  segment_number?: number | null;
  test_category?: string;
  is_active?: boolean;
  file_name?: string;
}

// Helper function for file uploads
const apiRequestWithFile = async <T>(
  endpoint: string,
  formData: FormData,
  options: RequestInit = {}
): Promise<T> => {
  const token = getToken();
  
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }

  // Don't set Content-Type for FormData - browser will set it with boundary
  const response = await fetch(endpoint, {
    ...options,
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg = errorData.error || errorData.message || errorData.detail || `HTTP error! status: ${response.status}`;
    console.error('[API Upload Error]', {
      status: response.status,
      statusText: response.statusText,
      endpoint,
      errorData,
      extractedMessage: msg
    });
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return response.json();
};

// Material Management API calls (Admin only)
export const materialAPI = {
  // Upload system audio file
  uploadMaterial: async (data: UploadMaterialData): Promise<AudioFile> => {
    const formData = new FormData();
    formData.append('audio_file', data.audio_file);
    if (data.segment_number !== undefined) {
      formData.append('segment_number', data.segment_number.toString());
    }
    if (data.test_category) {
      formData.append('test_category', data.test_category);
    }

    return apiRequestWithFile<AudioFile>(API_ENDPOINTS.MATERIAL_UPLOAD, formData, {
      method: 'POST',
    });
  },

  // Get list of all system audio files (optional source filter: 'lab')
  getMaterials: async (params?: { source?: string }): Promise<AudioFile[]> => {
    const url = params?.source
      ? `${API_ENDPOINTS.MATERIAL_LIST}?source=${params.source}`
      : API_ENDPOINTS.MATERIAL_LIST;
    return apiRequest<AudioFile[]>(url, { method: 'GET' });
  },

  // Delete system audio file
  deleteMaterial: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.MATERIAL_DELETE(id), {
      method: 'DELETE',
    });
  },

  // Update system audio metadata
  updateMaterial: async (id: number, data: UpdateMaterialData): Promise<AudioFile> => {
    return apiRequest<AudioFile>(API_ENDPOINTS.MATERIAL_UPDATE(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/** Admin: Test Rules page PDFs (Garage); download names fixed for learners. */
export interface TestRulesPdfSlotInfo {
  name: string;
  file_size: number;
  description: string;
  updated_at: string;
}

export type TestRulesPdfsMap = Record<string, TestRulesPdfSlotInfo | null>;

export const TEST_RULES_PDF_SLOTS: ReadonlyArray<{
  name: string;
  title: string;
  blurb: string;
}> = [
  {
    name: 'ccl-instructions.pdf',
    title: 'CCL instructions',
    blurb: 'Credentialed Community Language Test — shown on Test Rules with download.',
  },
  {
    name: 'naati-identification.pdf',
    title: 'NAATI identification',
    blurb: 'How NAATI identifies you — Test Rules second PDF.',
  },
];

export const testRulesPdfsAPI = {
  list: async (): Promise<TestRulesPdfsMap> => {
    return apiRequest<TestRulesPdfsMap>(API_ENDPOINTS.ADMIN_TEST_RULES_PDFS, { method: 'GET' });
  },

  upload: async (logicalName: string, pdfFile: File): Promise<TestRulesPdfSlotInfo> => {
    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('name', logicalName);
    return apiRequestWithFile<TestRulesPdfSlotInfo>(API_ENDPOINTS.ADMIN_TEST_RULES_PDF_UPLOAD, formData, {
      method: 'POST',
    });
  },
};

export interface AutoplayChimeConfigDTO {
  autoplay_chime_audio_id: number | null;
  file_name: string | null;
  stream_url?: string | null;
}

/** Autoplay chime: one system sound before auto-start recording (dialogue / mock). */
export const autoplayChimeAPI = {
  get: async (): Promise<AutoplayChimeConfigDTO> => {
    return apiRequest<AutoplayChimeConfigDTO>(API_ENDPOINTS.AUTOPLAY_CHIME, { method: 'GET' });
  },
  getAdmin: async (): Promise<AutoplayChimeConfigDTO> => {
    return apiRequest<AutoplayChimeConfigDTO>(API_ENDPOINTS.ADMIN_AUTOPLAY_CHIME, { method: 'GET' });
  },
  updateAdmin: async (autoplay_chime_audio_id: number | null): Promise<AutoplayChimeConfigDTO> => {
    return apiRequest<AutoplayChimeConfigDTO>(API_ENDPOINTS.ADMIN_AUTOPLAY_CHIME, {
      method: 'PATCH',
      body: JSON.stringify({ autoplay_chime_audio_id }),
    });
  },
};

// Lab API (Admin - ElevenLabs TTS)
export interface LabVoice {
  voice_id: string;
  name: string;
}

export const labAPI = {
  getVoices: async (): Promise<{ voices: LabVoice[] }> => {
    return apiRequest<{ voices: LabVoice[] }>(API_ENDPOINTS.LAB_VOICES, { method: 'GET' });
  },
  generate: async (data: { text: string; voice_id: string; language_code?: string }): Promise<{ audio_base64: string }> => {
    return apiRequest<{ audio_base64: string }>(API_ENDPOINTS.LAB_GENERATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  save: async (data: { audio_base64: string; file_name: string }): Promise<AudioFile> => {
    return apiRequest<AudioFile>(API_ENDPOINTS.LAB_SAVE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getItems: async (): Promise<AudioFile[]> => {
    return apiRequest<AudioFile[]>(API_ENDPOINTS.LAB_ITEMS, { method: 'GET' });
  },
};

// System Audio Content (source text + ChatGPT translations)
export const audioContentAPI = {
  getContent: async (audioId: number): Promise<{ source_text: string; translations: string[] }> => {
    return apiRequest(API_ENDPOINTS.AUDIO_CONTENT(audioId), { method: 'GET' });
  },
  updateContent: async (audioId: number, data: { source_text?: string; translations?: string[] }): Promise<{ source_text: string; translations: string[] }> => {
    return apiRequest(API_ENDPOINTS.AUDIO_CONTENT(audioId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  generateTranslations: async (audioId: number, data: { text: string; source_lang: string; target_lang: string }): Promise<{ translations: string[] }> => {
    return apiRequest(API_ENDPOINTS.AUDIO_GENERATE_TRANSLATIONS(audioId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  saveTranslations: async (audioId: number, data: { source_text?: string; translations: string[] }): Promise<{ source_text: string; translations: string[] }> => {
    return apiRequest(API_ENDPOINTS.AUDIO_SAVE_TRANSLATIONS(audioId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export interface BulkContentItem {
  audio_id: number;
  order: number;
  file_name: string;
  source_text: string;
  source_lang: string;
  target_lang: string;
  translations: string[];
  error?: string | null;
}

export const dialogueBulkContentAPI = {
  parse: async (
    topicId: number,
    partId: number,
    transcript: string,
  ): Promise<{
    items: BulkContentItem[];
    warnings: string[];
    voice_count: number;
    parsed_turn_count: number;
  }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_PART_BULK_CONTENT(topicId, partId), {
      method: 'POST',
      body: JSON.stringify({ action: 'parse', transcript }),
    });
  },
  translate: async (
    topicId: number,
    partId: number,
    items: BulkContentItem[],
  ): Promise<{
    items: BulkContentItem[];
    errors: { audio_id: number; file_name?: string; error: string }[];
  }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_PART_BULK_CONTENT(topicId, partId), {
      method: 'POST',
      body: JSON.stringify({ action: 'translate', items }),
    });
  },
  save: async (
    topicId: number,
    partId: number,
    items: BulkContentItem[],
  ): Promise<{ saved: BulkContentItem[]; count: number }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_PART_BULK_CONTENT(topicId, partId), {
      method: 'POST',
      body: JSON.stringify({ action: 'save', items }),
    });
  },
};

// Structural Segment interfaces
export interface SegmentAudio {
  id: number;
  segment: number;
  audio_file: number;
  audio_file_data: AudioFile;
  order: number;
  created_at: string;
}

export interface StructuralSegment {
  id: number;
  name: string;
  description: string | null;
  segment_number: number | null;
  test_category: string | null;
  is_active: boolean;
  /** Available to all signed-in users without subscription */
  is_free_public?: boolean;
  created_at: string;
  updated_at: string;
  audios: SegmentAudio[];
  audio_count: number;
}

export interface CreateStructuralSegmentData {
  name: string;
  description?: string;
  segment_number?: number;
  test_category?: string;
  is_active?: boolean;
  is_free_public?: boolean;
}

export interface UpdateStructuralSegmentData {
  name?: string;
  description?: string;
  segment_number?: number;
  test_category?: string;
  is_active?: boolean;
  is_free_public?: boolean;
}

export interface AddAudioToSegmentData {
  audio_file_id: number;
  order?: number;
}

// Structural Segments API calls (Admin only)
export const structuralSegmentsAPI = {
  // Get list of all structural segments
  getSegments: async (params?: MaterialListQuery): Promise<PaginatedResponse<StructuralSegment>> => {
    const data = await apiRequest<PaginatedResponse<StructuralSegment> | StructuralSegment[]>(
      `${API_ENDPOINTS.STRUCTURAL_SEGMENTS}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  // Create new structural segment
  createSegment: async (data: CreateStructuralSegmentData): Promise<StructuralSegment> => {
    return apiRequest<StructuralSegment>(API_ENDPOINTS.STRUCTURAL_SEGMENTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get segment details
  getSegment: async (id: number): Promise<StructuralSegment> => {
    return apiRequest<StructuralSegment>(API_ENDPOINTS.STRUCTURAL_SEGMENT_DETAIL(id), {
      method: 'GET',
    });
  },

  // Update segment
  updateSegment: async (id: number, data: UpdateStructuralSegmentData): Promise<StructuralSegment> => {
    return apiRequest<StructuralSegment>(API_ENDPOINTS.STRUCTURAL_SEGMENT_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Delete segment
  deleteSegment: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.STRUCTURAL_SEGMENT_DETAIL(id), {
      method: 'DELETE',
    });
  },

  // Add audio to segment
  addAudioToSegment: async (segmentId: number, data: AddAudioToSegmentData): Promise<SegmentAudio> => {
    return apiRequest<SegmentAudio>(API_ENDPOINTS.STRUCTURAL_SEGMENT_ADD_AUDIO(segmentId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Remove audio from segment
  removeAudioFromSegment: async (segmentId: number, segmentAudioId: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.STRUCTURAL_SEGMENT_AUDIO_DETAIL(segmentId, segmentAudioId), {
      method: 'DELETE',
    });
  },

  // Update audio order in segment
  updateAudioOrder: async (segmentId: number, segmentAudioId: number, order: number): Promise<SegmentAudio> => {
    return apiRequest<SegmentAudio>(API_ENDPOINTS.STRUCTURAL_SEGMENT_AUDIO_DETAIL(segmentId, segmentAudioId), {
      method: 'PATCH',
      body: JSON.stringify({ order }),
    });
  },
};

// Public Structural Segments API (for regular users)
export const publicStructuralSegmentsAPI = {
  getActiveSegments: async (params?: MaterialListQuery): Promise<PaginatedResponse<StructuralSegment>> => {
    const data = await apiRequest<PaginatedResponse<StructuralSegment> | StructuralSegment[]>(
      `${API_ENDPOINTS.PUBLIC_STRUCTURAL_SEGMENTS}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },
  // Get segment details by ID
  getSegment: async (id: number): Promise<StructuralSegment> => {
    return apiRequest<StructuralSegment>(API_ENDPOINTS.PUBLIC_STRUCTURAL_SEGMENT_DETAIL(id), {
      method: 'GET',
    });
  },
  // Check if segment is completed by user
  checkCompletion: async (id: number): Promise<{ is_completed: boolean; total_audios: number; completed_audios: number; message: string }> => {
    return apiRequest<{ is_completed: boolean; total_audios: number; completed_audios: number; message: string }>(
      API_ENDPOINTS.CHECK_SEGMENT_COMPLETION(id),
      { method: 'GET' }
    );
  },

  batchCheckCompletion: async (segmentIds: number[]): Promise<Record<string, boolean>> => {
    const data = await apiRequest<{ results: Record<string, boolean> }>(API_ENDPOINTS.BATCH_SEGMENT_COMPLETION, {
      method: 'POST',
      body: JSON.stringify({ segment_ids: segmentIds }),
    });
    return data.results;
  },
};

// User Recordings API
export const recordingsAPI = {
  // Upload user recording - supports both segment (Structural Segment) and dialogue (category dialogue)
  uploadRecording: async (
    audioBlob: Blob,
    segmentId: number | null,
    dialogueId: number | null,
    audioIndex: number,
    attempts: number = 0,
    duration?: number,
    transcript?: string
  ): Promise<AudioFile> => {
    const formData = new FormData();
    const id = segmentId ?? dialogueId ?? 0;
    const filename = `recording_${id}_${audioIndex}_${Date.now()}.webm`;
    formData.append('audio_file', audioBlob, filename);
    if (segmentId) formData.append('segment_id', segmentId.toString());
    if (dialogueId) formData.append('dialogue_id', dialogueId.toString());
    formData.append('attempts', attempts.toString());
    formData.append('audio_index', audioIndex.toString());
    if (duration !== undefined && duration > 0) {
      formData.append('duration', duration.toString());
    }
    if (transcript && transcript.trim()) {
      formData.append('transcript', transcript.trim());
    }
    
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    const response = await fetch(API_ENDPOINTS.UPLOAD_RECORDING, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  /** Register a full practice run without uploading audio (segment or dialogue category only). */
  registerPracticeSession: async (params: {
    segmentId: number | null;
    dialogueId: number | null;
    clips: { audio_index: number; attempts: number }[];
  }): Promise<{ ok: boolean; message: string }> => {
    const body: Record<string, unknown> = { clips: params.clips };
    if (params.segmentId != null) body.segment_id = params.segmentId;
    if (params.dialogueId != null) body.dialogue_id = params.dialogueId;
    return apiRequest<{ ok: boolean; message: string }>(API_ENDPOINTS.REGISTER_PRACTICE_SESSION, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Submit mock test (creates MockTestSubmission + MockTestSubmissionAudio)
  submitMockTest: async (
    dialogueId: number,
    recordings: { blob: Blob; transcript?: string; duration?: number }[]
  ): Promise<{ submission_id: number }> => {
    const formData = new FormData();
    formData.append('dialogue_id', dialogueId.toString());
    recordings.forEach((r, i) => {
      const filename = `mock_${dialogueId}_${i}_${Date.now()}.webm`;
      formData.append(`audio_${i}`, r.blob, filename);
      if (r.transcript?.trim()) formData.append(`transcript_${i}`, r.transcript.trim());
      if (r.duration !== undefined && r.duration > 0) formData.append(`duration_${i}`, r.duration.toString());
    });

    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Token ${token}`;

    const response = await fetch(API_ENDPOINTS.MOCK_SUBMIT, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  },

  // Get stream URL for audio
  getStreamURL: (audioId: number): string => {
    return API_ENDPOINTS.STREAM_AUDIO(audioId);
  },
  
  // Fetch audio as blob with authentication
  fetchAudioBlob: async (audioId: number): Promise<string> => {
    console.log('[API] fetchAudioBlob called for audioId:', audioId);
    const token = getToken();
    const url = API_ENDPOINTS.STREAM_AUDIO(audioId);
    
    console.log('[API] Request URL:', url);
    console.log('[API] Token exists:', !!token);
    
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    console.log('[API] Making fetch request...');
    let response: Response;
    try {
      response = await fetch(url, {
      method: 'GET',
      headers,
    });
    } catch (networkError: any) {
      console.error('[API] Network error:', networkError);
      throw new Error('Network error: Could not connect to server. Please check your connection.');
    }
    
    console.log('[API] Response status:', response.status, response.statusText);
    console.log('[API] Response headers:', {
      'content-type': response.headers.get('content-type'),
      'content-length': response.headers.get('content-length'),
      'accept-ranges': response.headers.get('accept-ranges')
    });
    
    if (!response.ok) {
      // Try to parse error response
      let errorData: any = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          errorData = await response.json();
        } catch (e) {
          console.warn('[API] Could not parse error response as JSON');
        }
      }
      
      console.error('[API] Response error:', errorData);
      
      // Handle specific error codes
      if (response.status === 503) {
        throw new Error(errorData.error || 'Storage service is not available. Please contact administrator.');
      } else if (response.status === 404) {
        throw new Error(errorData.error || 'Audio file not found.');
      } else if (response.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (response.status === 403) {
        throw new Error('Permission denied. You do not have access to this file.');
      } else {
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }
    }
    
    console.log('[API] Converting response to blob...');
    let blob: Blob;
    try {
      blob = await response.blob();
    } catch (blobError: any) {
      console.error('[API] Error creating blob:', blobError);
      throw new Error('Failed to process audio file. The file may be corrupted.');
    }
    
    console.log('[API] Blob created:', {
      size: blob.size,
      type: blob.type
    });
    
    // Check if blob is empty or too small (might be an error response)
    if (blob.size === 0) {
      throw new Error('Received empty audio file. Please try again.');
    }
    
    const blobURL = URL.createObjectURL(blob);
    console.log('[API] Blob URL created:', blobURL.substring(0, 50) + '...');
    return blobURL;
  },
};

// Subscription interfaces
export interface Subscription {
  id: number;
  user: number;
  user_email: string;
  user_name?: string;
  start_date: string;
  expires_at: string;
  is_active: boolean;
  created_by?: number;
  created_by_email?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  days_remaining: number;
  is_valid: boolean;
}

export interface CreateSubscriptionData {
  user_email: string;
  duration_days: number;
  notes?: string;
}

export interface UpdateSubscriptionData {
  expires_at?: string;
  is_active?: boolean;
  notes?: string;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  subscription: Subscription | null;
}

// Subscription API calls
export const subscriptionAPI = {
  // List all subscriptions (Admin only)
  listSubscriptions: async (): Promise<Subscription[]> => {
    return apiRequest<Subscription[]>(API_ENDPOINTS.SUBSCRIPTIONS_LIST, {
      method: 'GET',
    });
  },

  // Create new subscription (Admin only)
  createSubscription: async (data: CreateSubscriptionData): Promise<Subscription> => {
    return apiRequest<Subscription>(API_ENDPOINTS.SUBSCRIPTIONS_CREATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get subscription by ID (Admin only)
  getSubscription: async (id: number): Promise<Subscription> => {
    return apiRequest<Subscription>(API_ENDPOINTS.SUBSCRIPTION_DETAIL(id), {
      method: 'GET',
    });
  },

  // Update subscription (Admin only)
  updateSubscription: async (id: number, data: UpdateSubscriptionData): Promise<Subscription> => {
    return apiRequest<Subscription>(API_ENDPOINTS.SUBSCRIPTION_UPDATE(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete subscription (Admin only)
  deleteSubscription: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.SUBSCRIPTION_DELETE(id), {
      method: 'DELETE',
    });
  },

  // Get current user's subscription status
  getSubscriptionStatus: async (): Promise<SubscriptionStatus> => {
    return apiRequest<SubscriptionStatus>(API_ENDPOINTS.SUBSCRIPTION_STATUS, {
      method: 'GET',
    });
  },
};

// Podcast interfaces
export interface PodcastWatchProgress {
  progress_seconds: number;
  progress_percentage: number;
  last_watched_at?: string;
}

export interface Podcast {
  id: number;
  name: string;
  video_garage_key: string;
  thumbnail_garage_key?: string;
  video_file_size: number;
  video_file_size_mb: number;
  thumbnail_file_size?: number;
  thumbnail_file_size_mb?: number;
  video_mime_type: string;
  thumbnail_mime_type?: string;
  duration?: number;
  description?: string;
  is_active: boolean;
  is_free_public?: boolean;
  views_count: number;
  created_by?: number;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
  video_stream_url: string;
  thumbnail_url?: string;
  watch_progress?: PodcastWatchProgress | null;
}

export interface CreatePodcastData {
  name: string;
  description?: string;
  /** When creating via admin FormData, append as string "true"/"false". */
  is_free_public?: boolean;
}

// Podcast API calls (Public - for authenticated users)
export const podcastsAPI = {
  // List all active podcasts
  listPodcasts: async (params?: MaterialListQuery): Promise<PaginatedResponse<Podcast>> => {
    const data = await apiRequest<PaginatedResponse<Podcast> | Podcast[]>(
      `${API_ENDPOINTS.PODCASTS_LIST}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  // Get podcast by ID
  getPodcast: async (id: number): Promise<Podcast> => {
    console.log('[API] getPodcast called for id:', id);
    console.log('[API] Endpoint:', API_ENDPOINTS.PODCAST_DETAIL(id));
    try {
      const result = await apiRequest<Podcast>(API_ENDPOINTS.PODCAST_DETAIL(id), {
        method: 'GET',
      });
      console.log('[API] getPodcast result:', {
        id: result.id,
        name: result.name,
        video_stream_url: result.video_stream_url ? `${result.video_stream_url.substring(0, 50)}...` : 'null',
        video_garage_key: result.video_garage_key
      });
      return result;
    } catch (error) {
      console.error('[API] getPodcast error:', error);
      throw error;
    }
  },

  // Increment podcast views
  incrementViews: async (id: number): Promise<Podcast> => {
    return apiRequest<Podcast>(API_ENDPOINTS.PODCAST_INCREMENT_VIEWS(id), {
      method: 'POST',
    });
  },

  // Get stream URL for video (with authentication token)
  getStreamURL: (id: number): string => {
    const token = getToken();
    const url = API_ENDPOINTS.PODCAST_STREAM(id);
    // Add token as query parameter for video element
    return token ? `${url}?token=${token}` : url;
  },

  /** Thumbnail for <img src> — browsers do not send Authorization on image requests. */
  getThumbnailURL: (id: number): string => {
    const token = getToken();
    const url = API_ENDPOINTS.PODCAST_THUMBNAIL(id);
    return token ? `${url}?token=${encodeURIComponent(token)}` : url;
  },

  // Get watch progress for a podcast
  getWatchProgress: async (id: number): Promise<PodcastWatchProgress> => {
    return apiRequest<PodcastWatchProgress>(API_ENDPOINTS.PODCAST_WATCH_PROGRESS(id), {
      method: 'GET',
    });
  },

  // Update watch progress for a podcast (optionally update duration too)
  updateWatchProgress: async (id: number, progressSeconds: number, duration?: number): Promise<PodcastWatchProgress> => {
    const body: any = { progress_seconds: progressSeconds };
    if (duration !== undefined) {
      body.duration = duration;
    }
    return apiRequest<PodcastWatchProgress>(API_ENDPOINTS.PODCAST_WATCH_PROGRESS(id), {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
};

// Booking Service interfaces
export interface BookingService {
  id: number;
  title: string;
  description: string;
  image_garage_key?: string;
  image_file_size?: number;
  image_file_size_mb?: number;
  image_mime_type?: string;
  is_active: boolean;
  created_by?: number;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
  image_url?: string;
  has_user_request?: boolean;
}

export interface BookingRequest {
  id: number;
  user: number;
  user_email: string;
  user_name?: string;
  service: number;
  service_title: string;
  preferred_date?: string;
  preferred_time?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  admin_notes?: string;
  reviewed_by?: number;
  reviewed_by_email?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBookingServiceData {
  title: string;
  description: string;
  is_active?: boolean;
}

export interface CreateBookingRequestData {
  service: number;
  preferred_date?: string;
  preferred_time?: string;
  message?: string;
}

/** Booking service image for <img src> — browsers do not send Authorization on image requests. */
export function getBookingServiceImageURL(serviceId: number): string {
  const token = getToken();
  const url = API_ENDPOINTS.BOOKING_SERVICE_IMAGE(serviceId);
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}

// Booking API calls (Public - for authenticated users)
export const bookingAPI = {
  // List all active booking services
  listServices: async (): Promise<BookingService[]> => {
    return apiRequest<BookingService[]>(API_ENDPOINTS.BOOKING_SERVICES_LIST, {
      method: 'GET',
    });
  },

  // Create booking request
  createRequest: async (data: CreateBookingRequestData): Promise<BookingRequest> => {
    return apiRequest<BookingRequest>(API_ENDPOINTS.BOOKING_REQUEST_CREATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Admin Booking API calls
export const adminBookingAPI = {
  // List all booking services (Admin only)
  listServices: async (): Promise<BookingService[]> => {
    return apiRequest<BookingService[]>(API_ENDPOINTS.ADMIN_BOOKING_SERVICES, {
      method: 'GET',
    });
  },

  // Create booking service (Admin only)
  createService: async (formData: FormData): Promise<BookingService> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    const response = await fetch(API_ENDPOINTS.ADMIN_BOOKING_SERVICES, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create booking service');
    }

    return response.json();
  },

  // Get booking service by ID (Admin only)
  getService: async (id: number): Promise<BookingService> => {
    return apiRequest<BookingService>(API_ENDPOINTS.ADMIN_BOOKING_SERVICE_DETAIL(id), {
      method: 'GET',
    });
  },

  // Update booking service (Admin only)
  updateService: async (id: number, formData: FormData): Promise<BookingService> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    const response = await fetch(API_ENDPOINTS.ADMIN_BOOKING_SERVICE_DETAIL(id), {
      method: 'PATCH',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update booking service');
    }

    return response.json();
  },

  // Delete booking service (Admin only)
  deleteService: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_BOOKING_SERVICE_DETAIL(id), {
      method: 'DELETE',
    });
  },

  // List booking requests (Admin only)
  listRequests: async (status?: string): Promise<BookingRequest[]> => {
    const url = status 
      ? `${API_ENDPOINTS.ADMIN_BOOKING_REQUESTS}?status=${status}`
      : API_ENDPOINTS.ADMIN_BOOKING_REQUESTS;
    return apiRequest<BookingRequest[]>(url, {
      method: 'GET',
    });
  },

  // Update booking request status (Admin only)
  updateRequest: async (id: number, data: { status?: string; admin_notes?: string }): Promise<BookingRequest> => {
    return apiRequest<BookingRequest>(API_ENDPOINTS.ADMIN_BOOKING_REQUEST_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

export type OnlineCourseType = 'private' | 'workshop' | 'vip_feedback';

export interface OnlineCourse {
  id: number;
  course_type: OnlineCourseType;
  title: string;
  description: string;
  image_garage_key?: string | null;
  image_file_size?: number | null;
  image_file_size_mb?: number | null;
  image_mime_type?: string | null;
  sort_order: number;
  is_active: boolean;
  created_by?: number | null;
  created_by_email?: string | null;
  created_at: string;
  updated_at: string;
  image_url?: string | null;
  has_user_enrollment?: boolean;
}

export interface CourseEnrollment {
  id: number;
  user: number;
  user_email: string;
  user_name?: string;
  course: number;
  course_title: string;
  course_type: OnlineCourseType;
  applicant_message: string;
  contact_phone: string;
  contact_address: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  admin_notes?: string | null;
  reviewed_by?: number | null;
  reviewed_by_email?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export function getOnlineCourseImageURL(courseId: number): string {
  const token = getToken();
  const url = API_ENDPOINTS.ONLINE_COURSE_IMAGE(courseId);
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}

export const courseAPI = {
  listCourses: async (courseType: OnlineCourseType): Promise<OnlineCourse[]> => {
    const url = `${API_ENDPOINTS.ONLINE_COURSES_LIST}?course_type=${encodeURIComponent(courseType)}`;
    return apiRequest<OnlineCourse[]>(url, { method: 'GET' });
  },
  createEnrollment: async (data: {
    course: number;
    applicant_message?: string;
    contact_phone?: string;
    contact_address: string;
  }): Promise<CourseEnrollment> => {
    return apiRequest<CourseEnrollment>(API_ENDPOINTS.COURSE_ENROLLMENT_CREATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export const adminCourseAPI = {
  listCourses: async (courseType?: OnlineCourseType): Promise<OnlineCourse[]> => {
    const url = courseType
      ? `${API_ENDPOINTS.ADMIN_ONLINE_COURSES}?course_type=${encodeURIComponent(courseType)}`
      : API_ENDPOINTS.ADMIN_ONLINE_COURSES;
    return apiRequest<OnlineCourse[]>(url, { method: 'GET' });
  },
  createCourse: async (formData: FormData): Promise<OnlineCourse> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Token ${token}`;
    const response = await fetch(API_ENDPOINTS.ADMIN_ONLINE_COURSES, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || 'Failed to create course');
    }
    return response.json();
  },
  updateCourse: async (id: number, formData: FormData): Promise<OnlineCourse> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Token ${token}`;
    const response = await fetch(API_ENDPOINTS.ADMIN_ONLINE_COURSE_DETAIL(id), {
      method: 'PATCH',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || 'Failed to update course');
    }
    return response.json();
  },
  deleteCourse: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_ONLINE_COURSE_DETAIL(id), { method: 'DELETE' });
  },
  listEnrollments: async (filters?: { status?: string; course_type?: OnlineCourseType }): Promise<CourseEnrollment[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.course_type) params.set('course_type', filters.course_type);
    const q = params.toString();
    const url = q ? `${API_ENDPOINTS.ADMIN_COURSE_ENROLLMENTS}?${q}` : API_ENDPOINTS.ADMIN_COURSE_ENROLLMENTS;
    return apiRequest<CourseEnrollment[]>(url, { method: 'GET' });
  },
  updateEnrollment: async (
    id: number,
    data: { status?: string; admin_notes?: string }
  ): Promise<CourseEnrollment> => {
    return apiRequest<CourseEnrollment>(API_ENDPOINTS.ADMIN_COURSE_ENROLLMENT_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// Admin Podcast API calls
export const adminPodcastsAPI = {
  // List all podcasts (Admin only)
  listPodcasts: async (params?: MaterialListQuery): Promise<PaginatedResponse<Podcast>> => {
    const data = await apiRequest<PaginatedResponse<Podcast> | Podcast[]>(
      `${API_ENDPOINTS.ADMIN_PODCASTS_LIST}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  // Create new podcast (Admin only)
  createPodcast: async (formData: FormData): Promise<Podcast> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    // Do not set Content-Type: browser sets multipart boundary for FormData.

    const controller = new AbortController();
    const timeoutMs = 15 * 60 * 1000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(API_ENDPOINTS.ADMIN_PODCASTS_LIST, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });
    } catch (e: unknown) {
      const errName = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
      if (errName === 'AbortError') {
        throw new Error(
          'Upload timed out. If the server log shows "Invalid HTTP request received" (Uvicorn), ' +
            'set nginx for /api/ to proxy_http_version 1.1, proxy_set_header Connection "", and optionally proxy_request_buffering off ' +
            '(see deploy/nginx-site.example.conf). Also verify Garage (GARAGE_ENDPOINT) is reachable from the API process.'
        );
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  // Get podcast by ID (Admin only)
  getPodcast: async (id: number): Promise<Podcast> => {
    return apiRequest<Podcast>(API_ENDPOINTS.ADMIN_PODCAST_DETAIL(id), {
      method: 'GET',
    });
  },

  // Update podcast metadata (Admin only)
  updatePodcast: async (id: number, data: Partial<CreatePodcastData & { is_active?: boolean }>): Promise<Podcast> => {
    return apiRequest<Podcast>(API_ENDPOINTS.ADMIN_PODCAST_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Update podcast with optional video file (Admin only)
  updatePodcastForm: async (id: number, formData: FormData): Promise<Podcast> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }

    const controller = new AbortController();
    const timeoutMs = 15 * 60 * 1000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(API_ENDPOINTS.ADMIN_PODCAST_DETAIL(id), {
        method: 'PATCH',
        headers,
        body: formData,
        signal: controller.signal,
      });
    } catch (e: unknown) {
      const errName = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
      if (errName === 'AbortError') {
        throw new Error('Upload timed out while updating podcast.');
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  // Delete podcast (Admin only)
  deletePodcast: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_PODCAST_DETAIL(id), {
      method: 'DELETE',
    });
  },
};

// Dialogue Category interfaces
export interface DialogueCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  dialogues_count: number;
}

/** Part داخل یک Dialogue (دسته): چند ویس سیستم + بریفینگ اختیاری قبل از اولین ویس. */
export interface DialoguePart {
  id: number;
  dialogue: number;
  name: string;
  order: number;
  briefing_audio?: number | null;
  briefing_audio_detail?: (AudioFile & { source_text?: string; translations?: string[] }) | null;
  audios: DialoguePartAudio[];
  created_at: string;
  updated_at: string;
}

/** یک Dialogue زیر دستهٔ Dialogue Categories (لیست تخت audios + parts). */
export type DialogueUserFlag = 'red' | 'yellow' | 'green' | null;

export interface CategoryDialogue {
  id: number;
  category: number;
  category_name: string;
  /** Free without subscription (per dialogue topic). */
  is_free_public?: boolean;
  name: string;
  description?: string;
  is_active: boolean;
  enable_as_mock?: boolean;
  /** User-defined color flag for organizing dialogues. */
  user_flag?: DialogueUserFlag;
  /** How many times the user has practiced this dialogue (full sessions). */
  practice_session_count?: number;
  /** True if this user already has any mock submission for this dialogue (from API). */
  user_has_mock_submission?: boolean;
  /** Overall mock score (same averaging as result page) when status is graded; else null. */
  user_mock_overall_score?: number | null;
  created_at: string;
  updated_at: string;
  parts?: DialoguePart[];
  audios?: DialoguePartAudio[];
  audio_count: number;
}

export interface DialoguePartAudio {
  id: number;
  part: number;
  audio_file: number;
  audio_file_data: AudioFile;
  order: number;
  created_at: string;
}

export interface CreateDialogueCategoryData {
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface UpdateDialogueCategoryData {
  name?: string;
  description?: string;
  is_active?: boolean;
}

export interface CreateCategoryDialogueData {
  category: number;
  name: string;
  description?: string;
  is_active?: boolean;
  is_free_public?: boolean;
  enable_as_mock?: boolean;
}

export interface UpdateCategoryDialogueData {
  category?: number;
  name?: string;
  description?: string;
  is_active?: boolean;
  is_free_public?: boolean;
  enable_as_mock?: boolean;
}

export interface AddAudioToDialoguePartData {
  part_id: number;
  audio_file_id: number;
  order?: number;
}

export interface CreateDialoguePartData {
  name?: string;
  order?: number;
  briefing_audio?: number | null;
}

export interface UpdateDialoguePartData {
  name?: string;
  order?: number;
  briefing_audio?: number | null;
}

// Dialogue Categories API calls (Public - for authenticated users)
export const dialogueCategoriesAPI = {
  // List all active dialogue categories
  getCategories: async (): Promise<DialogueCategory[]> => {
    return apiRequest<DialogueCategory[]>(API_ENDPOINTS.DIALOGUE_CATEGORIES_LIST, {
      method: 'GET',
    });
  },

  // Dialogues in a category
  getDialogues: async (
    categoryId: number,
    params?: MaterialListQuery
  ): Promise<PaginatedResponse<CategoryDialogue>> => {
    const data = await apiRequest<PaginatedResponse<CategoryDialogue> | CategoryDialogue[]>(
      `${API_ENDPOINTS.DIALOGUES_IN_CATEGORY(categoryId)}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  getDialogue: async (dialogueId: number): Promise<CategoryDialogue> => {
    return apiRequest<CategoryDialogue>(API_ENDPOINTS.DIALOGUE_DETAIL(dialogueId), {
      method: 'GET',
    });
  },

  checkCompletion: async (dialogueId: number): Promise<{ is_completed: boolean; total_audios: number; completed_audios: number; message: string }> => {
    return apiRequest<{ is_completed: boolean; total_audios: number; completed_audios: number; message: string }>(
      API_ENDPOINTS.CHECK_DIALOGUE_COMPLETION(dialogueId),
      { method: 'GET' }
    );
  },

  batchCheckCompletion: async (dialogueIds: number[]): Promise<Record<string, boolean>> => {
    const data = await apiRequest<{ results: Record<string, boolean> }>(API_ENDPOINTS.BATCH_DIALOGUE_COMPLETION, {
      method: 'POST',
      body: JSON.stringify({ dialogue_ids: dialogueIds }),
    });
    return data.results;
  },

  updateUserProgress: async (
    dialogueId: number,
    flag: DialogueUserFlag
  ): Promise<{ user_flag: DialogueUserFlag; practice_session_count: number }> => {
    return apiRequest<{ user_flag: DialogueUserFlag; practice_session_count: number }>(
      API_ENDPOINTS.DIALOGUE_USER_PROGRESS(dialogueId),
      {
        method: 'PATCH',
        body: JSON.stringify({ flag }),
      }
    );
  },
};

export const mockDialoguesAPI = {
  list: async (): Promise<MockDialoguesListResponse> => {
    return apiRequest<MockDialoguesListResponse>(API_ENDPOINTS.MOCK_DIALOGUES_LIST, {
      method: 'GET',
    });
  },
};

export interface MockCreditsInfo {
  balance: number;
  can_take_mock: boolean;
  unlimited: boolean;
}

export interface MockDialoguesListResponse {
  dialogues: CategoryDialogue[];
  mock_credits: MockCreditsInfo;
}

export interface MockCreditLedgerEntry {
  id: number;
  entry_type: string;
  amount: number;
  balance_after: number;
  note: string;
  created_at: string;
}

export interface MockCreditSummary {
  balance: number;
  unlimited: boolean;
  can_take_mock: boolean;
  open_requests_count: number;
  ledger: MockCreditLedgerEntry[];
}

export interface MockCreditAttachment {
  id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  created_at: string;
  uploaded_by_email: string;
}

export interface MockCreditMessage {
  id: number;
  author_role: 'user' | 'admin';
  author_email: string;
  body: string;
  created_at: string;
  attachments: MockCreditAttachment[];
}

export interface MockCreditPurchaseRequest {
  id: number;
  requested_credits: number;
  granted_credits: number | null;
  status: 'open' | 'approved' | 'rejected' | 'cancelled';
  awaiting_user: boolean;
  contact_phone: string;
  contact_email: string;
  initial_message: string;
  admin_resolution_note: string;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  messages_count?: number;
  attachments_count?: number;
  last_message_preview?: string;
  last_message_at?: string | null;
  user_id?: number;
  user_email?: string;
  reviewed_by_email?: string | null;
  messages?: MockCreditMessage[];
  attachments?: MockCreditAttachment[];
  user_new_balance?: number;
}

export const mockCreditsAPI = {
  getSummary: async (): Promise<MockCreditSummary> => {
    return apiRequest<MockCreditSummary>(API_ENDPOINTS.MOCK_CREDITS_SUMMARY, { method: 'GET' });
  },
  listRequests: async (status?: string): Promise<MockCreditPurchaseRequest[]> => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiRequest<MockCreditPurchaseRequest[]>(`${API_ENDPOINTS.MOCK_CREDIT_REQUESTS}${q}`, { method: 'GET' });
  },
  getRequest: async (id: number): Promise<MockCreditPurchaseRequest> => {
    return apiRequest<MockCreditPurchaseRequest>(API_ENDPOINTS.MOCK_CREDIT_REQUEST_DETAIL(id), { method: 'GET' });
  },
  createRequest: async (data: {
    requested_credits: number;
    contact_phone?: string;
    contact_email?: string;
    initial_message?: string;
    file?: File | null;
  }): Promise<MockCreditPurchaseRequest> => {
    const formData = new FormData();
    formData.append('requested_credits', String(data.requested_credits));
    if (data.contact_phone) formData.append('contact_phone', data.contact_phone);
    if (data.contact_email) formData.append('contact_email', data.contact_email);
    if (data.initial_message) formData.append('initial_message', data.initial_message);
    if (data.file) formData.append('file', data.file);
    return apiRequestWithFile<MockCreditPurchaseRequest>(API_ENDPOINTS.MOCK_CREDIT_REQUESTS, formData, { method: 'POST' });
  },
  addMessage: async (id: number, body: string, file?: File | null): Promise<MockCreditPurchaseRequest> => {
    const formData = new FormData();
    if (body.trim()) formData.append('body', body.trim());
    if (file) formData.append('file', file);
    return apiRequestWithFile<MockCreditPurchaseRequest>(
      API_ENDPOINTS.MOCK_CREDIT_REQUEST_MESSAGES(id),
      formData,
      { method: 'POST' }
    );
  },
  cancelRequest: async (id: number): Promise<MockCreditPurchaseRequest> => {
    return apiRequest<MockCreditPurchaseRequest>(API_ENDPOINTS.MOCK_CREDIT_REQUEST_CANCEL(id), { method: 'POST' });
  },
};

export interface MockCreditTicketStats {
  open_total: number;
  awaiting_admin: number;
  awaiting_user: number;
}

export const adminMockCreditsAPI = {
  getStats: async (): Promise<MockCreditTicketStats> => {
    return apiRequest<MockCreditTicketStats>(API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUEST_STATS, { method: 'GET' });
  },
  listRequests: async (params?: { status?: string; awaiting_user?: string; search?: string }): Promise<MockCreditPurchaseRequest[]> => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.awaiting_user) sp.set('awaiting_user', params.awaiting_user);
    if (params?.search) sp.set('search', params.search);
    const q = sp.toString() ? `?${sp.toString()}` : '';
    return apiRequest<MockCreditPurchaseRequest[]>(`${API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUESTS}${q}`, { method: 'GET' });
  },
  getRequest: async (id: number): Promise<MockCreditPurchaseRequest> => {
    return apiRequest<MockCreditPurchaseRequest>(API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUEST_DETAIL(id), { method: 'GET' });
  },
  reply: async (id: number, body: string, file?: File | null): Promise<MockCreditPurchaseRequest> => {
    const formData = new FormData();
    if (body.trim()) formData.append('body', body.trim());
    if (file) formData.append('file', file);
    return apiRequestWithFile<MockCreditPurchaseRequest>(
      API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUEST_REPLY(id),
      formData,
      { method: 'POST' }
    );
  },
  approve: async (
    id: number,
    data: { granted_credits?: number; admin_resolution_note?: string; body?: string }
  ): Promise<MockCreditPurchaseRequest> => {
    return apiRequest<MockCreditPurchaseRequest>(API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUEST_APPROVE(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  reject: async (id: number, admin_resolution_note: string, body?: string): Promise<MockCreditPurchaseRequest> => {
    return apiRequest<MockCreditPurchaseRequest>(API_ENDPOINTS.ADMIN_MOCK_CREDIT_REQUEST_REJECT(id), {
      method: 'POST',
      body: JSON.stringify({ admin_resolution_note, body }),
    });
  },
  adjustCredits: async (userId: number, amount: number, note?: string): Promise<{ user_id: number; user_email: string; balance: number }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_USER_MOCK_CREDITS(userId), {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    });
  },
};

export type SubscriptionPaymentMethodId = 'card_to_card' | 'direct_rial' | 'direct_usd' | 'direct_crypto';

export interface SubscriptionPaymentMethodOption {
  id: SubscriptionPaymentMethodId;
  label: string;
  enabled: boolean;
}

export interface SubscriptionPlanConfig {
  price_usd: string;
  price_irr: number;
  duration_days: number;
  card_to_card_instructions?: string;
  payment_methods: SubscriptionPaymentMethodOption[];
  payment_card_to_card_enabled?: boolean;
  payment_direct_rial_enabled?: boolean;
  payment_direct_usd_enabled?: boolean;
  payment_direct_crypto_enabled?: boolean;
  updated_at?: string;
}

export interface SubscriptionPurchaseSummary {
  has_subscription: boolean;
  subscription: {
    id: number;
    expires_at: string;
    days_remaining: number;
    is_valid: boolean;
  } | null;
  open_requests_count: number;
  plan: SubscriptionPlanConfig;
}

export interface SubscriptionPurchaseAttachment {
  id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  created_at: string;
  uploaded_by_email: string;
}

export interface SubscriptionPurchaseMessage {
  id: number;
  author_role: 'user' | 'admin';
  author_email: string;
  body: string;
  created_at: string;
  attachments: SubscriptionPurchaseAttachment[];
}

export interface SubscriptionPurchaseRequest {
  id: number;
  payment_method: SubscriptionPaymentMethodId;
  payment_method_label: string;
  price_usd_snapshot: string;
  price_irr_snapshot: number;
  duration_days_snapshot: number;
  status: 'open' | 'approved' | 'rejected' | 'cancelled';
  awaiting_user: boolean;
  contact_phone: string;
  contact_email: string;
  initial_message: string;
  admin_resolution_note: string;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  messages_count: number;
  attachments_count: number;
  last_message_preview: string;
  last_message_at: string | null;
  granted_subscription_id?: number | null;
  user_id?: number;
  user_email?: string;
  reviewed_by_email?: string | null;
  messages?: SubscriptionPurchaseMessage[];
  attachments?: SubscriptionPurchaseAttachment[];
  granted_subscription?: {
    id: number;
    expires_at: string;
    days_remaining: number;
  };
}

export interface SubscriptionPurchaseTicketStats {
  open_total: number;
  awaiting_admin: number;
  awaiting_user: number;
}

export const subscriptionPurchaseAPI = {
  getSummary: async (): Promise<SubscriptionPurchaseSummary> => {
    return apiRequest<SubscriptionPurchaseSummary>(API_ENDPOINTS.SUBSCRIPTION_PURCHASE_SUMMARY, { method: 'GET' });
  },
  listRequests: async (status?: string): Promise<SubscriptionPurchaseRequest[]> => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiRequest<SubscriptionPurchaseRequest[]>(`${API_ENDPOINTS.SUBSCRIPTION_PURCHASE_REQUESTS}${q}`, {
      method: 'GET',
    });
  },
  getRequest: async (id: number): Promise<SubscriptionPurchaseRequest> => {
    return apiRequest<SubscriptionPurchaseRequest>(API_ENDPOINTS.SUBSCRIPTION_PURCHASE_REQUEST_DETAIL(id), {
      method: 'GET',
    });
  },
  createRequest: async (data: {
    payment_method: SubscriptionPaymentMethodId;
    contact_phone?: string;
    contact_email?: string;
    initial_message?: string;
    file: File;
  }): Promise<SubscriptionPurchaseRequest> => {
    const formData = new FormData();
    formData.append('payment_method', data.payment_method);
    if (data.contact_phone) formData.append('contact_phone', data.contact_phone);
    if (data.contact_email) formData.append('contact_email', data.contact_email);
    if (data.initial_message) formData.append('initial_message', data.initial_message);
    formData.append('file', data.file);
    return apiRequestWithFile<SubscriptionPurchaseRequest>(API_ENDPOINTS.SUBSCRIPTION_PURCHASE_REQUESTS, formData, {
      method: 'POST',
    });
  },
  addMessage: async (id: number, body: string, file?: File | null): Promise<SubscriptionPurchaseRequest> => {
    const formData = new FormData();
    if (body) formData.append('body', body);
    if (file) formData.append('file', file);
    return apiRequestWithFile<SubscriptionPurchaseRequest>(
      API_ENDPOINTS.SUBSCRIPTION_PURCHASE_REQUEST_MESSAGES(id),
      formData,
      { method: 'POST' }
    );
  },
  cancelRequest: async (id: number): Promise<SubscriptionPurchaseRequest> => {
    return apiRequest<SubscriptionPurchaseRequest>(API_ENDPOINTS.SUBSCRIPTION_PURCHASE_REQUEST_CANCEL(id), {
      method: 'POST',
    });
  },
};

export const adminSubscriptionPurchaseAPI = {
  getConfig: async (): Promise<SubscriptionPlanConfig> => {
    return apiRequest<SubscriptionPlanConfig>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_CONFIG, { method: 'GET' });
  },
  updateConfig: async (data: Partial<SubscriptionPlanConfig>): Promise<SubscriptionPlanConfig> => {
    return apiRequest<SubscriptionPlanConfig>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_CONFIG, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  getStats: async (): Promise<SubscriptionPurchaseTicketStats> => {
    return apiRequest<SubscriptionPurchaseTicketStats>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUEST_STATS, {
      method: 'GET',
    });
  },
  listRequests: async (params?: {
    status?: string;
    awaiting_user?: string;
    search?: string;
  }): Promise<SubscriptionPurchaseRequest[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.awaiting_user) searchParams.set('awaiting_user', params.awaiting_user);
    if (params?.search) searchParams.set('search', params.search);
    const q = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return apiRequest<SubscriptionPurchaseRequest[]>(`${API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUESTS}${q}`, {
      method: 'GET',
    });
  },
  getRequest: async (id: number): Promise<SubscriptionPurchaseRequest> => {
    return apiRequest<SubscriptionPurchaseRequest>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUEST_DETAIL(id), {
      method: 'GET',
    });
  },
  reply: async (id: number, body: string, file?: File | null): Promise<SubscriptionPurchaseRequest> => {
    const formData = new FormData();
    if (body) formData.append('body', body);
    if (file) formData.append('file', file);
    return apiRequestWithFile<SubscriptionPurchaseRequest>(
      API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUEST_REPLY(id),
      formData,
      { method: 'POST' }
    );
  },
  approve: async (
    id: number,
    data: { admin_resolution_note?: string; body?: string }
  ): Promise<SubscriptionPurchaseRequest> => {
    return apiRequest<SubscriptionPurchaseRequest>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUEST_APPROVE(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  reject: async (id: number, admin_resolution_note: string, body?: string): Promise<SubscriptionPurchaseRequest> => {
    return apiRequest<SubscriptionPurchaseRequest>(API_ENDPOINTS.ADMIN_SUBSCRIPTION_PURCHASE_REQUEST_REJECT(id), {
      method: 'POST',
      body: JSON.stringify({ admin_resolution_note, body }),
    });
  },
};

export interface MyMockSubmission {
  id: number;
  dialogue_name: string;
  dialogue_id: number;
  category_name: string;
  status: string;
  created_at: string;
  /** Set when status is `graded`; same formula as mock result overall. */
  overall_score?: number | null;
}

export interface MockResultCriterion {
  key: string;
  label: string;
  score: number;
}

/** Per recording segment: `order` is 0-based index in the dialogue. */
export interface MockGradeByAudioRow {
  order: number;
  /** Candidate's recording; playable via authenticated stream API. */
  user_recording_id?: number;
  /** Backend alias (same as user_recording_id). */
  rec_id?: number;
  distortion: number;
  unjustified_omission: number;
  unjustified_insertion: number;
  inappropriate_register: number;
  unidiomatic_expression: number;
  grammar_syntax_error: number;
  spelling_error: number;
  punctuation_error: number;
}

export interface MockUserRecordingOrderRow {
  order: number;
  user_recording_id: number | null;
}

export interface MockResult {
  id: number;
  dialogue_id?: number;
  dialogue_name: string;
  category_name?: string;
  status: string;
  created_at?: string;
  message?: string;
  /** Optional note from admin, shown to the candidate on the result page. */
  admin_overall_feedback?: string;
  overall_score?: number;
  criteria?: MockResultCriterion[];
  grades_by_audio?: MockGradeByAudioRow[];
  /** همهٔ آیتم‌های صوتی نمره دارند (برای دادهٔ قدیمی ناقص ممکن است false باشد) */
  grading_complete?: boolean;
  /** Every segment order → learner recording id (for playback when grades row omits id). */
  user_recordings_by_order?: MockUserRecordingOrderRow[];
  /** Same length and order as `grades_by_audio`; playback fallback if row lacks id fields. */
  learner_recording_ids?: (number | null)[];
}

function normalizeMockResultPayload(raw: MockResult): MockResult {
  const g = raw.grades_by_audio;
  const parallel = raw.learner_recording_ids;
  let grades = g;
  if (g?.length) {
    grades = g.map((row, i) => {
      const fromParallel =
        parallel && parallel.length === g.length
          ? normalizeRecordingId(parallel[i] as number | null | undefined)
          : undefined;
      const merged =
        normalizeRecordingId(row.user_recording_id) ??
        normalizeRecordingId((row as MockGradeByAudioRow).rec_id) ??
        fromParallel;
      if (merged != null && (row.user_recording_id == null || row.user_recording_id === undefined)) {
        return { ...row, user_recording_id: merged };
      }
      return row;
    });
  }
  let urbo = raw.user_recordings_by_order;
  if ((!urbo || urbo.length === 0) && grades?.length) {
    urbo = grades.map((row) => ({
      order: row.order,
      user_recording_id: row.user_recording_id ?? null,
    }));
  }
  return { ...raw, grades_by_audio: grades, user_recordings_by_order: urbo };
}

function normalizeRecordingId(v: number | string | null | undefined): number | undefined {
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export const mockSubmissionsAPI = {
  list: async (): Promise<MyMockSubmission[]> => {
    return apiRequest<MyMockSubmission[]>(API_ENDPOINTS.MOCK_MY_SUBMISSIONS, { method: 'GET' });
  },
  getResult: async (submissionId: number): Promise<MockResult> => {
    const raw = await apiRequest<MockResult>(API_ENDPOINTS.MOCK_RESULT(submissionId), { method: 'GET' });
    return normalizeMockResultPayload(raw);
  },
};

// Admin Dialogue Categories API calls
export const adminDialogueCategoriesAPI = {
  // List all dialogue categories (Admin only)
  getCategories: async (): Promise<DialogueCategory[]> => {
    return apiRequest<DialogueCategory[]>(API_ENDPOINTS.ADMIN_DIALOGUE_CATEGORIES, {
      method: 'GET',
    });
  },

  // Create dialogue category (Admin only)
  createCategory: async (data: CreateDialogueCategoryData): Promise<DialogueCategory> => {
    return apiRequest<DialogueCategory>(API_ENDPOINTS.ADMIN_DIALOGUE_CATEGORIES, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get category by ID (Admin only)
  getCategory: async (id: number): Promise<DialogueCategory> => {
    return apiRequest<DialogueCategory>(API_ENDPOINTS.ADMIN_DIALOGUE_CATEGORY_DETAIL(id), {
      method: 'GET',
    });
  },

  // Update category (Admin only)
  updateCategory: async (id: number, data: UpdateDialogueCategoryData): Promise<DialogueCategory> => {
    return apiRequest<DialogueCategory>(API_ENDPOINTS.ADMIN_DIALOGUE_CATEGORY_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Delete category (Admin only)
  deleteCategory: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_CATEGORY_DETAIL(id), {
      method: 'DELETE',
    });
  },

  getDialogues: async (
    categoryId: number,
    params?: MaterialListQuery
  ): Promise<PaginatedResponse<CategoryDialogue>> => {
    const data = await apiRequest<PaginatedResponse<CategoryDialogue> | CategoryDialogue[]>(
      `${API_ENDPOINTS.ADMIN_DIALOGUES_IN_CATEGORY(categoryId)}${buildListQuery(params)}`,
      { method: 'GET' }
    );
    return normalizePaginated(data);
  },

  createDialogue: async (categoryId: number, data: CreateCategoryDialogueData): Promise<CategoryDialogue> => {
    return apiRequest<CategoryDialogue>(API_ENDPOINTS.ADMIN_DIALOGUES_IN_CATEGORY(categoryId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDialogue: async (id: number): Promise<CategoryDialogue> => {
    return apiRequest<CategoryDialogue>(API_ENDPOINTS.ADMIN_CATEGORY_DIALOGUE_DETAIL(id), {
      method: 'GET',
    });
  },

  updateDialogue: async (id: number, data: UpdateCategoryDialogueData): Promise<CategoryDialogue> => {
    return apiRequest<CategoryDialogue>(API_ENDPOINTS.ADMIN_CATEGORY_DIALOGUE_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteDialogue: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_CATEGORY_DIALOGUE_DETAIL(id), {
      method: 'DELETE',
    });
  },

  addAudioToDialoguePart: async (dialogueId: number, data: AddAudioToDialoguePartData): Promise<DialoguePartAudio> => {
    return apiRequest<DialoguePartAudio>(API_ENDPOINTS.ADMIN_DIALOGUE_ADD_AUDIO(dialogueId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createDialoguePart: async (dialogueId: number, data: CreateDialoguePartData): Promise<DialoguePart> => {
    return apiRequest<DialoguePart>(API_ENDPOINTS.ADMIN_DIALOGUE_PARTS(dialogueId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateDialoguePart: async (dialogueId: number, partId: number, data: UpdateDialoguePartData): Promise<DialoguePart> => {
    return apiRequest<DialoguePart>(API_ENDPOINTS.ADMIN_DIALOGUE_PART_DETAIL(dialogueId, partId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteDialoguePart: async (dialogueId: number, partId: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_PART_DETAIL(dialogueId, partId), {
      method: 'DELETE',
    });
  },

  removeDialoguePartAudio: async (dialogueId: number, linkId: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_DIALOGUE_AUDIO_DETAIL(dialogueId, linkId), {
      method: 'DELETE',
    });
  },

  updateDialoguePartAudioOrder: async (dialogueId: number, linkId: number, order: number): Promise<DialoguePartAudio> => {
    return apiRequest<DialoguePartAudio>(API_ENDPOINTS.ADMIN_DIALOGUE_AUDIO_DETAIL(dialogueId, linkId), {
      method: 'PATCH',
      body: JSON.stringify({ order }),
    });
  },
};

// --- NAATI AI Support ---

export interface SupportBotConfigPublic {
  bot_name: string;
  is_enabled: boolean;
  welcome_message: string;
  show_on_public_pages?: boolean;
}

export interface SupportBotConfigAdmin extends SupportBotConfigPublic {
  system_prompt_extra: string;
  model_name: string;
  embedding_model: string;
  max_context_messages: number;
  max_user_message_chars: number;
  rag_top_k: number;
  guest_hourly_limit: number;
  user_hourly_limit: number;
  escalation_hint: string;
  updated_at: string;
}

export interface SupportChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  sources: { id: number; title: string; category: string }[];
  created_at: string;
}

export interface SupportConversation {
  id: number;
  status: 'active' | 'escalated' | 'closed';
  guest_token: string;
  user_email: string | null;
  escalated_at: string | null;
  escalation_note: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  messages?: SupportChatMessage[];
}

export interface SupportKnowledgeArticle {
  id: number;
  title: string;
  content: string;
  category: string;
  keywords: string;
  sort_order: number;
  is_active: boolean;
  updated_at: string;
  has_embedding?: boolean;
  embedding_updated_at?: string | null;
}

export interface OpenAIStatus {
  key_configured: boolean;
  connected: boolean;
  message: string;
  chat_models: string[];
  embedding_models: string[];
}

const SUPPORT_GUEST_TOKEN_KEY = 'naati_support_guest_token';

export const getSupportGuestToken = (): string | null => {
  return localStorage.getItem(SUPPORT_GUEST_TOKEN_KEY);
};

export const setSupportGuestToken = (token: string): void => {
  localStorage.setItem(SUPPORT_GUEST_TOKEN_KEY, token);
};

const supportHeaders = (): Record<string, string> => {
  const guest = getSupportGuestToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (guest) headers['X-Support-Guest-Token'] = guest;
  return headers;
};

const supportRequest = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const token = getToken();
  const headers: Record<string, string> = {
    ...supportHeaders(),
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Token ${token}`;

  const response = await fetch(endpoint, { ...options, headers });
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const msg =
      (typeof errorData.error === 'string' && errorData.error) ||
      (typeof errorData.detail === 'string' && errorData.detail) ||
      `HTTP error! status: ${response.status}`;
    throw new Error(msg);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
};

export const supportAPI = {
  getConfig: async (): Promise<SupportBotConfigPublic> => {
    return supportRequest<SupportBotConfigPublic>(API_ENDPOINTS.SUPPORT_CONFIG);
  },

  sendMessage: async (message: string, conversationId?: number): Promise<{
    conversation_id: number;
    guest_token: string;
    user_message: SupportChatMessage;
    assistant_message: SupportChatMessage;
  }> => {
    const data = await supportRequest<{
      conversation_id: number;
      guest_token: string;
      user_message: SupportChatMessage;
      assistant_message: SupportChatMessage;
    }>(API_ENDPOINTS.SUPPORT_CHAT, {
      method: 'POST',
      body: JSON.stringify({ message, conversation_id: conversationId ?? null }),
    });
    if (data.guest_token) setSupportGuestToken(data.guest_token);
    return data;
  },

  getConversation: async (id: number): Promise<SupportConversation> => {
    return supportRequest<SupportConversation>(API_ENDPOINTS.SUPPORT_CONVERSATION(id));
  },

  escalate: async (id: number, note?: string): Promise<SupportConversation> => {
    return supportRequest<SupportConversation>(API_ENDPOINTS.SUPPORT_ESCALATE(id), {
      method: 'POST',
      body: JSON.stringify({ note: note || '' }),
    });
  },
};

export const adminSupportAPI = {
  getOpenAIStatus: async (): Promise<OpenAIStatus> => {
    return apiRequest<OpenAIStatus>(API_ENDPOINTS.ADMIN_SUPPORT_OPENAI_STATUS);
  },

  getConfig: async (): Promise<SupportBotConfigAdmin> => {
    return apiRequest<SupportBotConfigAdmin>(API_ENDPOINTS.ADMIN_SUPPORT_CONFIG);
  },

  updateConfig: async (payload: Partial<SupportBotConfigAdmin>): Promise<SupportBotConfigAdmin> => {
    return apiRequest<SupportBotConfigAdmin>(API_ENDPOINTS.ADMIN_SUPPORT_CONFIG, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  listKnowledge: async (params?: { category?: string; is_active?: string; q?: string }): Promise<SupportKnowledgeArticle[]> => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.is_active) qs.set('is_active', params.is_active);
    if (params?.q) qs.set('q', params.q);
    const url = qs.toString()
      ? `${API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE}?${qs}`
      : API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE;
    const data = await apiRequest<{ results: SupportKnowledgeArticle[] }>(url);
    return data.results;
  },

  createKnowledge: async (payload: Partial<SupportKnowledgeArticle>): Promise<SupportKnowledgeArticle> => {
    return apiRequest<SupportKnowledgeArticle>(API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateKnowledge: async (id: number, payload: Partial<SupportKnowledgeArticle> & { refresh_embedding?: boolean }): Promise<SupportKnowledgeArticle> => {
    return apiRequest<SupportKnowledgeArticle>(API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteKnowledge: async (id: number): Promise<void> => {
    await apiRequest(API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE_DETAIL(id), { method: 'DELETE' });
  },

  refreshEmbedding: async (id: number): Promise<SupportKnowledgeArticle> => {
    return apiRequest<SupportKnowledgeArticle>(API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE_REFRESH(id), {
      method: 'POST',
    });
  },

  reindexAll: async (): Promise<{ indexed: number; total: number; errors: { id: number; title: string; error: string }[] }> => {
    return apiRequest(API_ENDPOINTS.ADMIN_SUPPORT_KNOWLEDGE_REINDEX, { method: 'POST' });
  },

  listConversations: async (params?: { status?: string; q?: string; limit?: number }): Promise<SupportConversation[]> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.limit) qs.set('limit', String(params.limit));
    const url = qs.toString()
      ? `${API_ENDPOINTS.ADMIN_SUPPORT_CONVERSATIONS}?${qs}`
      : API_ENDPOINTS.ADMIN_SUPPORT_CONVERSATIONS;
    const data = await apiRequest<{ results: SupportConversation[] }>(url);
    return data.results;
  },

  getConversation: async (id: number): Promise<SupportConversation> => {
    return apiRequest<SupportConversation>(API_ENDPOINTS.ADMIN_SUPPORT_CONVERSATION_DETAIL(id));
  },

  closeConversation: async (id: number): Promise<SupportConversation> => {
    return apiRequest<SupportConversation>(API_ENDPOINTS.ADMIN_SUPPORT_CONVERSATION_CLOSE(id), {
      method: 'POST',
    });
  },
};

